import { useEffect, useState } from "react";
import { promptDialog } from "@/shared/ui";
import type { GroupMaterialInfo, MaterialConfiguratorState } from "../../../../shared/api-contract";
import { useViewer } from "../../../../state/ViewerProvider";
import { useDomainState } from "../../../../state/editor/useEditor";
import { useCaps } from "../../CapsProvider";
import { Icon } from "../../../kit";
import { MiniBtn, Row } from "../../inspector/rows";

/** Read-only material identity (v31 `getGroupMaterialInfo`) — the uuid row. */
function MaterialUuidRow({ groupId }: { groupId: string }) {
  const { client } = useViewer();
  const r = client.ring;
  const [info, setInfo] = useState<GroupMaterialInfo | null>(null);
  useEffect(() => {
    let alive = true;
    void Promise.resolve(r.getGroupMaterialInfo(groupId)).then((i) => { if (alive) setInfo(i); }).catch(() => {});
    return () => { alive = false; };
  }, [r, groupId]);
  if (!info) return null;
  return (
    <Row label="uuid">
      <span className="truncate text-[11px] text-ed-text-3" title={info.uuid}>{info.uuid}</span>
    </Row>
  );
}

/**
 * Material Configurator (v22, iJewel) — per-material switchable variations.
 * Select a part, snapshot its current material as a variation, then switch
 * between variations (the buyer-facing customization). Gem + metal supported.
 *
 * "Show All" (webgi: hidden when a material IS selected) browses every
 * group's variations at once; "Apply All" mirrors webgi's literal semantics
 * (`this.variations.forEach(e=>this.applyVariation(e,e.materials[0].uuid))`)
 * — it re-applies every configured group's FIRST variation, scene-wide, not
 * just the currently selected group's active one.
 */
export function MaterialConfiguratorControl() {
  const { client } = useViewer();
  const r = client.ring;
  const caps = useCaps();
  const groupId = useDomainState("selection") as string | null;
  const [state, setState] = useState<MaterialConfiguratorState | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [allStates, setAllStates] = useState<MaterialConfiguratorState[]>([]);

  const refresh = () => { if (groupId) void r.getMaterialConfigurator(groupId).then(setState).catch(() => setState(null)); };
  useEffect(() => { if (groupId) r.getMaterialConfigurator(groupId).then(setState).catch(() => setState(null)); else setState(null); }, [groupId, r]);

  const refreshAll = () => void r.listMaterialConfigurators().then(setAllStates).catch(() => setAllStates([]));
  useEffect(() => { if (!groupId && showAll) refreshAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [groupId, showAll]);

  const applyIn = (gid: string, id: string) => void r.applyMaterialVariation(gid, id).then((s) => {
    if (gid === groupId) setState(s);
    if (showAll) refreshAll();
  });
  const removeIn = (gid: string, id: string) => {
    r.removeMaterialVariation(gid, id);
    if (gid === groupId) refresh();
    if (showAll) refreshAll();
  };
  const renameIn = async (gid: string, id: string, cur: string) => {
    const n = await promptDialog({ title: "Variation name", label: "Name", defaultValue: cur });
    if (n == null) return;
    r.renameMaterialVariation(gid, id, n);
    if (gid === groupId) refresh();
    if (showAll) refreshAll();
  };

  const applyAll = () => {
    void r.listMaterialConfigurators().then((groups) => {
      for (const g of groups) {
        const v = g.variations[0];
        if (v) void r.applyMaterialVariation(g.groupId, v.id).then((s) => { if (g.groupId === groupId) setState(s); });
      }
      if (showAll) refreshAll();
    });
  };

  if (!groupId) {
    return (
      <div className="flex flex-col gap-1.5 px-3 py-1.5">
        <div className="flex flex-wrap items-center gap-1.5">
          <MiniBtn icon="done_all" onClick={applyAll}>Apply All</MiniBtn>
          <MiniBtn icon="visibility" active={showAll} onClick={() => setShowAll((v) => !v)}>Show All</MiniBtn>
        </div>
        {!showAll ? (
          <span className="text-[11px] text-ed-text-3">Select a part to see or add variations, or enable “Show All”.</span>
        ) : allStates.length === 0 ? (
          <span className="text-[11px] text-ed-text-3">No configured materials yet.</span>
        ) : (
          <div className="flex flex-col gap-2.5">
            {allStates.filter((g) => g.variations.length > 0).map((g) => (
              <div key={g.groupId} className="flex flex-col gap-1">
                <span className="truncate text-[11px] font-medium text-ed-text-2">{g.groupLabel}</span>
                <VariationList
                  state={g}
                  onApply={(id) => applyIn(g.groupId, id)}
                  onRename={(id, cur) => renameIn(g.groupId, id, cur)}
                  onRemove={(id) => removeIn(g.groupId, id)}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const add = () => void r.addMaterialVariation(groupId).then(setState);
  const clear = () => void Promise.resolve(r.clearMaterialConfigurator(groupId)).then(() => setState(null));
  // v31: clearMaterialVariations empties the variation list but KEEPS the
  // configurator entry, distinct from clear()/"Remove completely" above which
  // drops the entry entirely. Falls back to the old (entry-removing) behavior
  // pre-v31, where the dedicated RPC doesn't exist yet.
  const clearVariations = () => {
    if (caps.v31) void Promise.resolve(r.clearMaterialVariations(groupId)).then(setState);
    else clear();
  };

  const variations = state?.variations ?? [];
  return (
    <div className="flex flex-col gap-1.5 px-3 py-1.5">
      {caps.v31 ? <MaterialUuidRow groupId={groupId} /> : null}
      <div className="flex flex-wrap items-center gap-1.5">
        <MiniBtn primary icon="add" onClick={add}>Add Variation</MiniBtn>
        <MiniBtn icon="done_all" onClick={applyAll}>Apply All</MiniBtn>
        <MiniBtn icon="refresh" onClick={refresh}>Refresh Ui</MiniBtn>
        {variations.length ? <MiniBtn icon="delete" onClick={clearVariations}>Clear variations</MiniBtn> : null}
        {variations.length ? <MiniBtn icon="delete_forever" onClick={clear}>Remove completely</MiniBtn> : null}
      </div>
      {variations.length === 0 ? (
        <span className="text-[11px] text-ed-text-3">Set this part's material, then “Add Variation” to capture it.</span>
      ) : (
        <VariationList
          state={state!}
          onApply={(id) => applyIn(groupId, id)}
          onRename={(id, cur) => renameIn(groupId, id, cur)}
          onRemove={(id) => removeIn(groupId, id)}
        />
      )}
    </div>
  );
}

function VariationList({
  state,
  onApply,
  onRename,
  onRemove,
}: {
  state: MaterialConfiguratorState;
  onApply: (id: string) => void;
  onRename: (id: string, cur: string) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <ul className="flex flex-col gap-1">
      {state.variations.map((v, i) => (
        <li key={v.id} className={`flex items-center gap-1.5 rounded border px-2 py-1 ${i === state.activeIndex ? "border-ed-accent bg-ed-active" : "border-ed-border bg-white"}`}>
          <button className="flex min-w-0 flex-1 items-center gap-1.5 text-left" onClick={() => onApply(v.id)} title="Apply variation">
            <Icon name="palette" size={14} className="shrink-0 text-ed-text-3" />
            <span className="truncate text-[12px] text-ed-text">{i + 1}. {v.name}</span>
          </button>
          <button className="shrink-0 text-ed-text-3 hover:text-ed-accent" onClick={() => onRename(v.id, v.name)} title="Rename"><Icon name="edit" size={14} /></button>
          <button className="shrink-0 text-ed-text-3 hover:text-red-500" onClick={() => onRemove(v.id)} title="Remove"><Icon name="close" size={14} /></button>
        </li>
      ))}
    </ul>
  );
}
